import React, { useState, useEffect } from 'react';
import { API_BASE } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { LogOut, Copy, Check, Clock, X, FileText, DollarSign, Calendar, CreditCard, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { TCSLogo } from '../components/TCSLogo';
import { NavArrows } from '../components/NavArrows';

const Payment = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [declaring, setDeclaring] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [infoRes, histRes] = await Promise.all([
        axios.get(`${API_BASE}/api/payment/info`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/payment/history`, { withCredentials: true })
      ]);
      setPaymentInfo(infoRes.data);
      setHistory(histRes.data);
    } catch (error) {
      console.error('Error loading payment data:', error);
      toast.error('Erro ao carregar dados de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const copyPixKey = () => {
    navigator.clipboard.writeText('81991517018');
    toast.success('Chave PIX copiada!');
  };

  const handleDeclarePayment = async () => {
    setDeclaring(true);
    try {
      await axios.post(`${API_BASE}/api/payment/declare`);
      toast.success('Pagamento notificado! Aguarde a confirmação do treinador.');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao notificar pagamento');
    } finally {
      setDeclaring(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-[#FFB800]/10', border: 'border-[#FFB800]', text: 'text-[#FFB800]', icon: Clock, label: 'Aguardando' },
      confirmed: { bg: 'bg-[#39FF14]/10', border: 'border-[#39FF14]', text: 'text-[#39FF14]', icon: Check, label: 'Confirmado' },
      rejected: { bg: 'bg-[#FF3366]/10', border: 'border-[#FF3366]', text: 'text-[#FF3366]', icon: X, label: 'Rejeitado' }
    };
    const s = styles[status] || styles.pending;
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 ${s.bg} ${s.border} border ${s.text} text-xs font-bold uppercase tracking-wider`}>
        <Icon className="w-3 h-3" />
        {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#00F0FF] text-xl font-bold tracking-wider uppercase">Carregando...</div>
      </div>
    );
  }

  const isOuro = paymentInfo?.plan === 'ouro';

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <NavArrows />
            <TCSLogo size="sm" showTagline={false} />
            <div>
              <h1 className="text-xl md:text-2xl font-black uppercase tracking-tighter text-[#00F0FF]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Mensalidade
              </h1>
              <p className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">
                Gestão do seu plano
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="bg-transparent border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none hidden md:inline-flex"
              data-testid="payment-back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="icon"
              className="bg-transparent border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366]/10 rounded-none"
              data-testid="payment-logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Plan Info Card - Plano Bronze como Principal */}
        <div className={`bg-[#121212] border-2 ${isOuro ? 'border-[#FFB800]' : 'border-[#00F0FF]'} p-8 mb-6`} data-testid="plan-info-card">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-1">Seu Plano</p>
              <h2 className={`text-4xl md:text-5xl font-black uppercase tracking-tighter ${isOuro ? 'text-[#FFB800]' : 'text-[#00F0FF]'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {paymentInfo?.plan_name}
              </h2>
              <p className="text-white mt-2">
                <span className={`text-3xl md:text-4xl font-black ${isOuro ? 'text-[#FFB800]' : 'text-[#00F0FF]'}`}>R$ {paymentInfo?.price?.toFixed(2).replace('.', ',')}</span>
                <span className="text-[#A1A1AA]"> /mês</span>
              </p>
              <p className="text-xs text-[#A1A1AA] mt-2 uppercase tracking-wider">
                <strong className="text-white">Vencimento:</strong> Todo dia {paymentInfo?.due_day} do mês
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-1">Status do Mês</p>
              {paymentInfo?.is_paid ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#39FF14]/10 border-2 border-[#39FF14]">
                  <Check className="w-5 h-5 text-[#39FF14]" />
                  <span className="text-[#39FF14] font-bold uppercase">Pago</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFB800]/10 border-2 border-[#FFB800]">
                  <Clock className="w-5 h-5 text-[#FFB800]" />
                  <span className="text-[#FFB800] font-bold uppercase">Pendente</span>
                </div>
              )}
            </div>
          </div>

          {/* Outstanding debt banner (carry-over from previous cycles) */}
          {paymentInfo?.debt_months > 1 && (
            <div className="bg-[#FF3366]/15 border-2 border-[#FF3366] p-4 mt-6" data-testid="payment-debt-banner">
              <p className="text-[#FF3366] font-bold uppercase tracking-wider text-sm mb-1">
                ⚠ Mensalidades em aberto: {paymentInfo.debt_months}
              </p>
              <p className="text-white text-sm">
                Você tem <strong>{paymentInfo.debt_months} mensalidades pendentes</strong>
                {paymentInfo?.unpaid_months?.length > 0 && ` (${paymentInfo.unpaid_months.join(', ')})`}.
                Valor total em aberto: <strong className="text-[#FF3366]">R$ {paymentInfo.total_debt?.toFixed(2).replace('.', ',')}</strong>.
                Quite o valor total para reativar o acesso completo.
              </p>
            </div>
          )}

          {/* Motivational Message */}
          <div className="bg-gradient-to-br from-[#00F0FF]/10 via-[#121212] to-[#39FF14]/5 border border-[#00F0FF]/40 p-5 md:p-6 mt-6" data-testid="motivational-payment-message">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#00F0FF] mb-3">Por que continuar?</p>
            <p className="text-white text-sm md:text-base leading-relaxed mb-4">
              Esse não é um boleto — é o <strong className="text-[#00F0FF]">investimento que muda a sua vida</strong>. Cada mensalidade garante a sua saúde física e mental, seu coração protegido e o seu desempenho elevado por quem entende e vive o esporte de alto rendimento.
            </p>
            <ul className="space-y-2 text-sm text-white/90 mb-4">
              <li className="flex items-start gap-2"><span className="text-[#39FF14] flex-shrink-0">▸</span> Acompanhamento <strong>direto com seu treinador e fisiologista esportivo</strong>, sem intermediário.</li>
              <li className="flex items-start gap-2"><span className="text-[#39FF14] flex-shrink-0">▸</span> <strong>Treinos de academia, ciclismo, nutrição e suplementação</strong> integrados — tudo em um só lugar.</li>
              <li className="flex items-start gap-2"><span className="text-[#39FF14] flex-shrink-0">▸</span> Acesso ao <strong>TrainingPeaks</strong> + plataforma exclusiva TCS com sua evolução em tempo real.</li>
              <li className="flex items-start gap-2"><span className="text-[#39FF14] flex-shrink-0">▸</span> <strong>Grupo VIP</strong> de atletas e suporte permanente.</li>
              <li className="flex items-start gap-2"><span className="text-[#39FF14] flex-shrink-0">▸</span> Sua <strong>saúde cardíaca monitorada</strong> diariamente — prevenção é sempre mais barata que tratamento.</li>
            </ul>
            <p className="text-sm md:text-base text-white leading-relaxed">
              Pelo preço de um par de pneus por mês, você tem o que <strong className="text-[#39FF14]">vale uma vida inteira de atleta saudável</strong>. Não interrompa. Não retroceda. Continue evoluindo com quem te entende.
            </p>
            <p className="text-xs text-[#A1A1AA] mt-3 italic">
              — Thyago Costa Silva, fisiologista esportivo e atleta profissional há 17 anos.
            </p>
          </div>

          {/* Warning about cancellation */}
          <div className="bg-[#FF3366]/10 border border-[#FF3366] p-4 mt-3">
            <p className="text-white text-sm">
              <strong className="text-[#FF3366]">⚠ Importante:</strong> Caso a mensalidade não seja paga até o vencimento, <strong>seu acesso será cancelado automaticamente</strong>. Mantenha sua mensalidade em dia para não perder o acompanhamento.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-[#00F0FF]" />
              <div>
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Próximo Vencimento</p>
                <p className="text-white font-semibold">
                  {new Date(paymentInfo?.next_due_date).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-[#00F0FF]" />
              <div>
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Valor a Pagar</p>
                <p className="text-white font-semibold">
                  R$ {paymentInfo?.price?.toFixed(2).replace('.', ',')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* PIX Info */}
        <div className="grid grid-cols-1 gap-6">
          {/* PIX Card */}
          <div className="bg-[#121212] border border-white/10 p-6" data-testid="pix-info-card">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-[#00F0FF]" />
              <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">Dados para Pagamento via PIX</h3>
            </div>

            <div className="space-y-4">
              <div className="bg-[#0A0A0A] p-4 border border-white/5">
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Chave PIX (Celular)</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-2xl font-bold text-[#00F0FF]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="pix-key-value">
                    81991517018
                  </p>
                  <Button
                    onClick={copyPixKey}
                    size="sm"
                    className="bg-[#00F0FF] hover:bg-[#4DFFFF] text-black rounded-none"
                    data-testid="copy-pix-button"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="bg-[#0A0A0A] p-4 border border-white/5">
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Nome do Beneficiário</p>
                <p className="text-lg font-bold text-white">THYAGO COSTA SILVA</p>
              </div>

              <div className="bg-[#0A0A0A] p-4 border border-white/5">
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Valor</p>
                <p className={`text-3xl font-black ${isOuro ? 'text-[#FFB800]' : 'text-[#00F0FF]'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  R$ {paymentInfo?.price?.toFixed(2).replace('.', ',')}
                </p>
              </div>

              <div className="bg-[#FFB800]/10 border border-[#FFB800] p-3">
                <p className="text-sm text-white">
                  <strong className="text-[#FFB800]">Instruções:</strong> Realize o PIX no valor exato até a data de vencimento. Após o pagamento, clique no botão abaixo para notificar o treinador.
                </p>
              </div>

              {/* Status / Action */}
              {paymentInfo?.is_paid ? (
                <div className="bg-[#39FF14]/10 border border-[#39FF14] p-4 flex items-center gap-3" data-testid="payment-confirmed-banner">
                  <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0" />
                  <div>
                    <p className="text-[#39FF14] font-bold uppercase text-sm">Mensalidade Confirmada</p>
                    <p className="text-xs text-white/80 mt-1">Pagamento do mês confirmado pelo treinador. Bom treino!</p>
                  </div>
                </div>
              ) : paymentInfo?.is_declared ? (
                <div className="bg-[#FFB800]/10 border border-[#FFB800] p-4 flex items-center gap-3" data-testid="payment-declared-banner">
                  <Clock className="h-5 w-5 text-[#FFB800] flex-shrink-0" />
                  <div>
                    <p className="text-[#FFB800] font-bold uppercase text-sm">Aguardando Confirmação</p>
                    <p className="text-xs text-white/80 mt-1">Você notificou o pagamento. O treinador irá confirmar em breve.</p>
                  </div>
                </div>
              ) : (paymentInfo?.days_until_due !== null && paymentInfo?.days_until_due !== undefined && paymentInfo.days_until_due > 0) ? (
                <div className="bg-[#00F0FF]/10 border border-[#00F0FF] p-4 flex items-center gap-3" data-testid="payment-locked-banner">
                  <Clock className="h-5 w-5 text-[#00F0FF] flex-shrink-0" />
                  <div>
                    <p className="text-[#00F0FF] font-bold uppercase text-sm">Vencimento em {paymentInfo.days_until_due} {paymentInfo.days_until_due === 1 ? 'dia' : 'dias'}</p>
                    <p className="text-xs text-white/80 mt-1">
                      Você poderá notificar o pagamento a partir de{' '}
                      <strong>{new Date(paymentInfo.next_due_date).toLocaleDateString('pt-BR')}</strong>.
                    </p>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={handleDeclarePayment}
                  disabled={declaring}
                  className="w-full bg-[#39FF14] hover:bg-[#39FF14]/80 text-black font-bold uppercase h-12 rounded-none"
                  data-testid="declare-payment-button"
                >
                  <Check className="h-4 w-4 mr-2" />
                  {declaring ? 'Enviando...' : 'Já efetuei o pagamento'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* History */}
        <div className="bg-[#121212] border border-white/10 p-6 mt-6" data-testid="history-card">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#00F0FF]" />
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">Histórico de Pagamentos</h3>
          </div>

          {history.length === 0 ? (
            <p className="text-[#A1A1AA] text-center py-8">Nenhum pagamento registrado ainda</p>
          ) : (
            <div className="space-y-2">
              {history.map((p) => (
                <div key={p._id} className="bg-[#0A0A0A] border border-white/5 p-4 flex items-center justify-between gap-4" data-testid={`payment-${p._id}`}>
                  <div className="flex-1">
                    <p className="text-white font-semibold">
                      Mensalidade {p.month} · R$ {p.amount?.toFixed(2).replace('.', ',')}
                    </p>
                    <p className="text-xs text-[#A1A1AA]">
                      Enviado em {new Date(p.submitted_at).toLocaleDateString('pt-BR')}
                      {p.notes && ` · ${p.notes}`}
                    </p>
                  </div>
                  {getStatusBadge(p.status)}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Payment;
